import { useState } from 'react';
import { useNotify, usePermissions, useRecordContext, useRefresh, type RaRecord } from 'react-admin';
import {
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SectionCard, fmtDate } from '../../shared/Panel';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { SUBCONTRACTOR_RULE_LABELS, setSubcontractorRule } from '../publications/publicationsApi';
import { recordScoped } from '../../shared/recordScoped';

/** Role, które mogą ustawić regułę — założenie robocze, patrz `publikacje/bramki.ts` w API. */
const EDITOR_ROLES = ['ADMIN', 'COMPANY_ADMIN'];

export const RULE_OPTIONS = [
  { kod: '', label: 'nieustalona — klient nie podał (publikacja po ręcznym potwierdzeniu)' },
  { kod: 'dozwolony', label: 'dozwolony' },
  { kod: 'warunkowy', label: 'warunkowy (publikacja po ręcznym potwierdzeniu)' },
  { kod: 'zakaz', label: 'zakaz (publikacja tylko przez jawny wyjątek uprawnionej osoby)' },
];

export const ruleColor = (rule: string | null | undefined): 'default' | 'success' | 'warning' | 'error' => {
  switch (rule) {
    case 'dozwolony':
      return 'success';
    case 'warunkowy':
      return 'warning';
    case 'zakaz':
      return 'error';
    default:
      return 'default';
  }
};

/**
 * „przez …” przy dacie ustawienia reguły. Nazwę konta podaje API
 * (`subcontractorSetBy`); gdy konta już nie ma, zostaje sam numer.
 */
export const setByLabel = (r: RaRecord): string => {
  const by = r.subcontractorSetBy as { username?: string | null } | null | undefined;
  if (by?.username) return ` przez ${by.username}`;
  return r.subcontractorSetById ? ` przez użytkownika #${String(r.subcontractorSetById)}` : '';
};

/**
 * Reguła dopuszczalności podwykonawcy przy kontrahencie (W16, 869f1ny5a).
 *
 * To jedyna NASZA dana na kartotece z TMS. Nieustalona nie znaczy „dozwolony":
 * do czasu listy od klienta każda publikacja zlecenia tego klienta wymaga
 * ręcznego potwierdzenia. Ustawia sekcja administracyjna; każda zmiana
 * zostawia wpis w dzienniku działań (kto, kiedy, z czego na co).
 */
const SubcontractorRuleSectionPanel = () => {
  const r = useRecordContext<RaRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions<string>();
  const [rule, setRule] = useState<string>((r?.subcontractorRule as string | null) ?? '');
  const [notes, setNotes] = useState<string>((r?.subcontractorNotes as string | null) ?? '');
  const [busy, setBusy] = useState(false);
  if (!r) return null;

  const current = (r.subcontractorRule as string | null) ?? null;
  const canEdit = EDITOR_ROLES.includes(permissions ?? '');

  const save = async () => {
    setBusy(true);
    try {
      await setSubcontractorRule(r.id as number, rule || null, notes);
      notify('Reguła podwykonawcy zapisana', { type: 'success' });
      refresh();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard title="Podwykonawca — reguła publikacji na giełdach">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <Chip
          size="small"
          color={ruleColor(current)}
          label={SUBCONTRACTOR_RULE_LABELS[current ?? 'nieustalona'] ?? current}
        />
        {r.subcontractorSetAt ? (
          <Typography variant="caption" color="text.secondary">
            ustawiono {fmtDate(r.subcontractorSetAt as string)}
            {setByLabel(r)}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            nikt jeszcze nie ustawił — wartość ma podać klient
          </Typography>
        )}
      </Stack>
      {r.subcontractorNotes ? (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {String(r.subcontractorNotes)}
        </Typography>
      ) : null}

      {canEdit ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }} useFlexGap>
          <FormControl size="small" fullWidth={false} sx={{ minWidth: 320 }}>
            <InputLabel id="regula-podwykonawcy">Reguła</InputLabel>
            <Select
              labelId="regula-podwykonawcy"
              label="Reguła"
              value={rule}
              onChange={(e) => setRule(String(e.target.value))}
            >
              {RULE_OPTIONS.map((o) => (
                <MenuItem key={o.kod || 'brak'} value={o.kod}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            fullWidth={false}
            label="Uwagi (skąd reguła, np. mail klienta z datą)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            // Etykieta (ok. 315 px) nie mieściła się w polu 320 px i ucinała się wielokropkiem —
            // pole rośnie do wolnego miejsca w wierszu (audyt układu 23.10.2026).
            sx={{ minWidth: 360, flex: '1 1 360px' }}
          />
          <Button variant="outlined" size="small" disabled={busy} onClick={() => void save()}>
            Zapisz regułę
          </Button>
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Regułę ustawia administrator (do czasu imiennej listy od klienta).
        </Typography>
      )}
    </SectionCard>
  );
};

/** Stan panelu należy do rekordu — przejście na inny rekord zaczyna od zera (`recordScoped`). */
export const SubcontractorRuleSection = recordScoped(SubcontractorRuleSectionPanel);
